import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { X, Cookie, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'dsh_cookie_consent';

export function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      // Short delay so the page loads first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  function accept() {
    localStorage.setItem(STORAGE_KEY, 'accepted');
    setVisible(false);
  }

  function decline() {
    localStorage.setItem(STORAGE_KEY, 'declined');
    setVisible(false);
  }

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 80 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 80 }}
          transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-50"
          role="dialog"
          aria-label="Cookie consent"
          data-testid="cookie-banner"
        >
          <div className="bg-card border border-border rounded-2xl shadow-2xl p-6">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center shrink-0">
                  <Cookie className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-bold text-sm">Cookies & Privacy</h3>
                  <p className="text-xs text-muted-foreground">Your privacy matters to us.</p>
                </div>
              </div>
              <button
                onClick={decline}
                className="text-muted-foreground hover:text-foreground transition-colors shrink-0 mt-0.5"
                aria-label="Schließen"
                data-testid="cookie-close-btn"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Body */}
            <p className="text-xs text-muted-foreground leading-relaxed mb-5">
              We only use technically necessary cookies required for the operation
              of this website. No tracking or advertising cookies are set without
              your consent.{' '}
              <Link
                to="/datenschutz"
                className="text-primary underline underline-offset-2 hover:no-underline"
              >
                Privacy Policy
              </Link>
              {' · '}
              <Link
                to="/cookie-settings"
                className="text-primary underline underline-offset-2 hover:no-underline"
              >
                Manage Settings
              </Link>
            </p>

            {/* Essential cookie info */}
            <div className="flex items-center gap-2 text-xs text-muted-foreground mb-5 bg-primary/5 rounded-lg px-3 py-2">
              <Shield className="w-3.5 h-3.5 text-primary shrink-0" />
              <span>Essential cookies only – no tracking, no Google Analytics.</span>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={decline}
                className="flex-1 border-border hover:border-primary/30 text-xs"
                data-testid="cookie-decline-btn"
              >
                Decline
              </Button>
              <Button
                size="sm"
                onClick={accept}
                className="flex-1 bg-primary hover:bg-primary/90 text-xs font-bold"
                data-testid="cookie-accept-btn"
              >
                Accept
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
